/** The member's personal Your IMAI chat page in a workspace. */
export function yourImaiHref(orgSlug: string): string {
  return `/${orgSlug}/imai`;
}

/**
 * Where a channel thread opens: WhatsApp and iMessage have their own pages
 * under `/imai`; the other channels open in place on the Your IMAI page.
 */
export function yourImaiChannelHref(orgSlug: string, channel: string): string {
  if (channel === 'main') return yourImaiHref(orgSlug);
  if (channel === 'whatsapp' || channel === 'imessage')
    return `${yourImaiHref(orgSlug)}/${channel}`;
  return `${yourImaiHref(orgSlug)}?channel=${encodeURIComponent(channel)}`;
}

/** Messages the conversation loads per page, on the server and the client. */
export const YOUR_IMAI_FEED_PAGE_SIZE = 40;

/** The workspace an `/<org>/imai` request opens, if it is one. */
export function yourImaiRouteOrgSlug(pathname: string | null): string | null {
  const segments =
    (pathname ?? '').split('?')[0]?.split('/').filter(Boolean) ?? [];
  const [orgSlug, route] = segments;
  return orgSlug && route === 'imai' && segments.length === 2 ? orgSlug : null;
}

/**
 * Whether `pathname` is one of this workspace's Your IMAI pages: the chat
 * or its settings. The floating IMAI dock stays off there; it would only
 * open the page the member is already on, and on a phone it covers the
 * settings' last controls.
 */
export function isYourImaiArea(
  pathname: string | null,
  orgSlug: string
): boolean {
  const [first, second] =
    (pathname ?? '').split('?')[0]?.split('/').filter(Boolean) ?? [];
  return first === orgSlug && second === 'imai';
}

/** The sections of the Your IMAI settings page, in navigation order. */
export const YOUR_IMAI_SETTINGS_SECTIONS = [
  'customize',
  'context',
  'personality',
  'email',
  'incoming',
  'payments',
] as const;

/** Sections shown only where their feature is on for the member. */
export function visibleYourImaiSettingsSections(features: {
  payments: boolean;
}): YourImaiSettingsSection[] {
  return YOUR_IMAI_SETTINGS_SECTIONS.filter(
    (section) => section !== 'payments' || features.payments
  );
}

export type YourImaiSettingsSection =
  (typeof YOUR_IMAI_SETTINGS_SECTIONS)[number];

export function isYourImaiSettingsSection(
  value: string | undefined
): value is YourImaiSettingsSection {
  return YOUR_IMAI_SETTINGS_SECTIONS.some((section) => section === value);
}

/** One section of the member's Your IMAI settings page. */
export function yourImaiSettingsHref(
  orgSlug: string,
  section: YourImaiSettingsSection = 'customize'
): string {
  return `${yourImaiHref(orgSlug)}/settings/${section}`;
}
